import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';

interface EFilingTypeData {
  orderType: 'e-filing';
  orderInfo: {
    state: string;
    county: string;
    filingType: string;
  };
  caseInfo: {
    caseTitle: string;
    caseCategoryName: string;
    caseType?: { code: string; name: string } | null;
    courtJurisdiction?: { code: string; name: string } | null;
    trialCourtCaseNumber?: string;
    premisesAddress?: {
      address1?: string;
      city?: string;
      state?: string;
      zip?: string;
    };
  };
  parties: Array<{
    partyType: string;
    roleCode: string;
    roleName: string;
    firstName?: string;
    lastName?: string;
    organizationName?: string;
    isSelfRepresented?: boolean;
  }>;
  attorneys?: Array<{
    firstName: string;
    lastName: string;
    barNumber: string;
    firmName?: string;
  }>;
}

interface OrderSummaryProps {
  typeData: unknown;
  brand: BrandConfig;
}

function extractEFilingData(typeData: unknown): EFilingTypeData | null {
  if (!typeData || typeof typeData !== 'object') return null;
  const data = typeData as Record<string, unknown>;

  // Direct shape: { orderType: 'e-filing', orderInfo, caseInfo, ... }
  if (data.orderType === 'e-filing' && data.orderInfo) {
    return data as unknown as EFilingTypeData;
  }

  // Legacy wrapped: { efilingState: { ... } }
  if (data.efilingState && typeof data.efilingState === 'object') {
    return data.efilingState as unknown as EFilingTypeData;
  }

  return null;
}

const FILING_TYPE_LABELS: Record<string, string> = {
  'case-initiation': 'Case Initiation',
  'subsequent-filing': 'Subsequent Filing',
};

const OrderSummary: React.FC<OrderSummaryProps> = ({ typeData, brand }) => {
  const data = extractEFilingData(typeData);
  if (!data) return null;

  const { orderInfo, caseInfo, parties, attorneys } = data;
  const hasPremises = caseInfo.premisesAddress?.address1;

  const getPartyName = (p: EFilingTypeData['parties'][0]) =>
    p.partyType === 'person'
      ? [p.firstName, p.lastName].filter(Boolean).join(' ')
      : p.organizationName || '';

  return (
    <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
        Filing Summary
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs" style={{ color: brand.colors.textLight }}>County</p>
          <p className="text-sm capitalize" style={{ color: brand.colors.text }}>
            {orderInfo.county.replace(/-/g, ' ')}
          </p>
        </div>
        {caseInfo.courtJurisdiction?.name && (
          <div>
            <p className="text-xs" style={{ color: brand.colors.textLight }}>Courthouse</p>
            <p className="text-sm" style={{ color: brand.colors.text }}>
              {caseInfo.courtJurisdiction.name}
            </p>
          </div>
        )}
        <div>
          <p className="text-xs" style={{ color: brand.colors.textLight }}>Case Category</p>
          <p className="text-sm" style={{ color: brand.colors.text }}>
            {caseInfo.caseCategoryName || '—'}
          </p>
        </div>
        {caseInfo.caseType?.name && (
          <div>
            <p className="text-xs" style={{ color: brand.colors.textLight }}>Case Type</p>
            <p className="text-sm" style={{ color: brand.colors.text }}>
              {caseInfo.caseType.name}
            </p>
          </div>
        )}
        <div>
          <p className="text-xs" style={{ color: brand.colors.textLight }}>Filing Type</p>
          <p className="text-sm" style={{ color: brand.colors.text }}>
            {FILING_TYPE_LABELS[orderInfo.filingType] || orderInfo.filingType || '—'}
          </p>
        </div>
        {caseInfo.trialCourtCaseNumber && (
          <div>
            <p className="text-xs" style={{ color: brand.colors.textLight }}>Case Number</p>
            <p className="text-sm font-mono" style={{ color: brand.colors.text }}>
              {caseInfo.trialCourtCaseNumber}
            </p>
          </div>
        )}
      </div>

      {/* Premises Address (UD cases) */}
      {hasPremises && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: brand.colors.border }}>
          <p className="text-xs" style={{ color: brand.colors.textLight }}>Premises Address</p>
          <p className="text-sm" style={{ color: brand.colors.text }}>
            {[
              caseInfo.premisesAddress?.address1,
              caseInfo.premisesAddress?.city,
              caseInfo.premisesAddress?.state,
              caseInfo.premisesAddress?.zip,
            ].filter(Boolean).join(', ')}
          </p>
        </div>
      )}

      {/* Parties */}
      {parties.length > 0 && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: brand.colors.border }}>
          <p className="text-xs font-medium mb-1" style={{ color: brand.colors.textLight }}>
            Parties ({parties.length})
          </p>
          <div className="space-y-1">
            {parties.map((p) => (
              <div key={`${p.roleCode}-${getPartyName(p)}`} className="flex justify-between text-sm">
                <span style={{ color: brand.colors.text }}>
                  {getPartyName(p)}
                  {p.partyType === 'organization' && (
                    <span className="text-xs ml-1" style={{ color: brand.colors.textLight }}>(Org)</span>
                  )}
                  {p.isSelfRepresented && (
                    <span className="text-xs ml-1" style={{ color: brand.colors.textLight }}>(Self-Rep)</span>
                  )}
                </span>
                <span className="text-xs" style={{ color: brand.colors.textLight }}>
                  {p.roleName}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attorneys */}
      {attorneys && attorneys.length > 0 && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: brand.colors.border }}>
          <p className="text-xs font-medium mb-1" style={{ color: brand.colors.textLight }}>
            Attorneys ({attorneys.length})
          </p>
          <div className="space-y-1">
            {attorneys.map((att) => (
              <div key={att.barNumber} className="text-sm" style={{ color: brand.colors.text }}>
                {att.firstName} {att.lastName}
                <span className="text-xs ml-2 font-mono" style={{ color: brand.colors.textLight }}>
                  Bar# {att.barNumber}
                </span>
                {att.firmName && (
                  <span className="text-xs ml-2" style={{ color: brand.colors.textLight }}>
                    {att.firmName}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderSummary;
